import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '../ui/button';
import { Download, X, FileText, Calendar } from 'lucide-react';
import { format } from 'date-fns';

const CustomReportPreviewModal = ({ report, onClose, onDownload }) => {
  if (!report) return null;

  const content = report.analysis || report.content || '';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[90vh] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-[#283F8B] to-[#108981] px-6 py-4 text-white flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white/15 rounded-lg mt-0.5">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold" data-testid="report-preview-title">{report.name}</h2>
              <div className="flex items-center gap-3 text-white/80 text-sm mt-0.5">
                <span className="capitalize">{(report.report_type || 'custom').replace(/_/g, ' ')}</span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {report.created_at ? format(new Date(report.created_at), 'MMMM d, yyyy') : 'N/A'}
                </span>
              </div>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 -mr-2 -mt-1" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Query */}
        {report.query && (
          <div className="px-6 py-3 bg-slate-50 border-b">
            <p className="text-sm text-muted-foreground"><strong>Query:</strong> {report.query}</p>
          </div>
        )}

        {/* Report Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5" data-testid="report-preview-content">
          {content ? (
            <div className="report-markdown prose prose-sm max-w-none prose-headings:text-[#283F8B] prose-strong:text-slate-800 prose-th:bg-slate-100 prose-th:border prose-td:border prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-1.5">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  table: ({ children }) => (
                    <div className="overflow-x-auto my-4 rounded-lg border">
                      <table className="w-full text-sm">{children}</table>
                    </div>
                  ),
                  thead: ({ children }) => (
                    <thead className="bg-[#283F8B]/5 text-[#283F8B] font-medium">{children}</thead>
                  ),
                  th: ({ children }) => (
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider border-b border-slate-200">{children}</th>
                  ),
                  td: ({ children }) => (
                    <td className="px-3 py-2 border-b border-slate-100 text-slate-700">{children}</td>
                  ),
                  tr: ({ children, ...props }) => (
                    <tr className="even:bg-slate-50/50 hover:bg-slate-50" {...props}>{children}</tr>
                  ),
                  h1: ({ children }) => (
                    <h1 className="text-xl font-bold text-[#283F8B] border-b-2 border-[#108981] pb-2 mb-4 mt-6">{children}</h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-lg font-semibold text-[#283F8B] mt-5 mb-2">{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-base font-semibold text-[#108981] mt-4 mb-1.5">{children}</h3>
                  ),
                  p: ({ children }) => (
                    <p className="text-sm text-slate-700 leading-relaxed mb-2">{children}</p>
                  ),
                  li: ({ children }) => (
                    <li className="text-sm text-slate-700 mb-1">{children}</li>
                  ),
                  strong: ({ children }) => (
                    <strong className="text-slate-900 font-semibold">{children}</strong>
                  ),
                  hr: () => (
                    <hr className="my-4 border-t border-[#108981]/30" />
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-[#108981] bg-[#108981]/5 pl-4 py-2 my-3 rounded-r-lg text-sm italic text-slate-600">{children}</blockquote>
                  ),
                }}
              />
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No report content available.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-slate-50 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Generated by AkountFly AI</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onDownload} data-testid="report-download-btn">
              <Download className="w-4 h-4 mr-1.5" /> Download PDF
            </Button>
            <Button size="sm" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomReportPreviewModal;
